import React, { useEffect, useState, Fragment } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, Transition, Listbox } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  GlobeAltIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../../utils/cn';
import * as API from '../../../../api';
import ConfirmAuthSheet from '../../../../screens/Booking/ConfirmAuthSheet';

// The header's sign-in controls live in a few sibling components, so they ask
// the Navbar (which owns the modal) to open via a DOM event rather than
// threading state through every one of them.
const AUTH_SHEET_EVENT = 'tafwija-open-auth-sheet';
export function openAuthSheet(mode) {
  // Already signed in? Don't flash the sheet open just to close it again —
  // the auth check inside would resolve immediately and bounce the user on.
  // Go straight to the account instead.
  const stored = JSON.parse(localStorage.getItem('auth') || '{}');
  if (Object.keys(stored).length) {
    window.location.assign('/account/orders');
    return;
  }
  window.dispatchEvent(new CustomEvent(AUTH_SHEET_EVENT, { detail: { mode } }));
}

/**
 * Tafwija v2 navbar — premium floating-card style.
 *
 * Layout: [primary nav | LOGO (centered) | lang + auth cluster]
 * - Floats over content via fixed positioning + backdrop blur
 * - Active route is highlighted with a soft brand-tinted pill
 * - Language switcher uses Headless UI Listbox; switches <html dir> via i18n.js handler
 * - Auth state hooks the same API.Auth() / API.AuthStorage used by the legacy header
 */

const LANGS = [
  { code: 'fr', label: 'FR', native: 'Français' },
  { code: 'en', label: 'EN', native: 'English' },
  { code: 'ar', label: 'AR', native: 'العربية' },
];

function useAuth() {
  const [auth, setAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  // Read the user fresh from localStorage so inline login/register reflects
  // immediately (the module-level API.AuthStorage snapshot can be stale).
  const [user, setUser] = useState(() => (JSON.parse(localStorage.getItem('auth') || '{}')).user);

  useEffect(() => {
    let alive = true;
    const check = () => {
      API.Auth()
        .then(() => {
          if (!alive) return;
          setAuth(true);
          setUser((JSON.parse(localStorage.getItem('auth') || '{}')).user);
        })
        .catch(() => alive && setAuth(false))
        .finally(() => alive && setLoading(false));
    };
    check();
    // Re-check whenever auth changes elsewhere (inline login/register, logout).
    window.addEventListener('tafwija-auth-changed', check);
    return () => {
      alive = false;
      window.removeEventListener('tafwija-auth-changed', check);
    };
  }, []);

  return { auth, loading, user };
}

function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const current = LANGS.find((l) => l.code === i18n.language?.split('-')[0]) || LANGS[0];

  return (
    <Listbox value={current.code} onChange={(code) => i18n.changeLanguage(code)}>
      <div className="relative">
        <Listbox.Button
          className={cn(
            // Icon-only round on mobile, pill with FULL language name on lg+
            'inline-flex items-center justify-center w-10 h-10 rounded-full',
            'lg:w-auto lg:h-auto lg:gap-1.5 lg:px-3 lg:py-2 lg:rounded-lg lg:text-sm lg:font-semibold',
            'text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30'
          )}
          aria-label={t('lang.switcher', 'Language')}
        >
          <GlobeAltIcon className="w-5 h-5 lg:w-4 lg:h-4" />
          <span className="hidden lg:inline">{current.native}</span>
          <ChevronDownIcon className="hidden lg:inline w-3.5 h-3.5 opacity-60" />
        </Listbox.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options
            className={cn(
              'absolute end-0 mt-2 w-40 origin-top-end',
              'bg-white border border-slate-200/60 rounded-xl shadow-elevated p-1',
              'focus:outline-none z-50'
            )}
          >
            {LANGS.map((lng) => (
              <Listbox.Option
                key={lng.code}
                value={lng.code}
                className={({ active, selected }) =>
                  cn(
                    'flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors',
                    active && 'bg-surface-subtle',
                    selected && 'text-brand-600 font-semibold'
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span>{lng.native}</span>
                    <span className={cn('text-xs', selected ? 'text-brand-500' : 'text-ink-subtle')}>
                      {lng.label}
                    </span>
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

function PrimaryNavLinks({ className = '' }) {
  const { t } = useTranslation();
  const links = [
    { to: '/', label: t('nav.home', 'Accueil'), exact: true },
    { to: '/events', label: t('nav.events', 'Activités') },
    { to: '/galleries', label: t('nav.galleries', 'Galerie') },
    { to: '/blog', label: t('nav.blog', 'Blog') },
    { to: '/contact', label: t('nav.contact', 'Contact') },
  ];
  return (
    <ul className={cn('flex items-center gap-1', className)}>
      {links.map((link) => (
        <li key={link.to}>
          <NavLink
            to={link.to}
            exact={link.exact}
            activeClassName="!text-brand-600 !bg-brand-50"
            className={cn(
              'px-3.5 py-2 rounded-full text-sm font-medium transition-colors',
              'text-ink-muted hover:text-ink hover:bg-surface-subtle'
            )}
          >
            {link.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function AuthCluster({ auth, user }) {
  const { t } = useTranslation();

  if (!auth) {
    return (
      <div className="flex items-center gap-2">
        {/* Desktop: two buttons, no icons.
            Yellow = secondary (Become a partner), navy = primary (Sign in).
            Uses both brand colors. */}
        <Link
          to="/become-partner"
          className={cn(
            'hidden lg:inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold',
            'bg-accent-500 text-brand-900 hover:bg-accent-400 transition-colors shadow-card'
          )}
        >
          {t('nav.becomePartner', 'Devenir partenaire')}
        </Link>
        <button
          type="button"
          onClick={openAuthSheet}
          className={cn(
            'hidden lg:inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold',
            'bg-brand-500 text-white hover:bg-brand-600 transition-colors shadow-card'
          )}
        >
          {t('nav.signIn', 'Se connecter')}
        </button>
        {/* Mobile / tablet: user icon opens a dropdown with sign-in + partner CTA */}
        <Menu as="div" className="lg:hidden relative">
          <Menu.Button
            aria-label={t('nav.signIn', 'Se connecter')}
            className={cn(
              'inline-flex items-center justify-center w-10 h-10 rounded-full',
              'text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30'
            )}
          >
            <UserCircleIcon className="w-5 h-5" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Menu.Items
              className={cn(
                'absolute end-0 mt-2 w-56 origin-top-end z-50',
                'bg-white border border-slate-200/60 rounded-xl shadow-elevated p-1',
                'focus:outline-none'
              )}
            >
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={openAuthSheet}
                    className={cn(
                      'block w-full text-start px-3 py-2 rounded-lg text-sm font-semibold transition-colors',
                      active ? 'bg-surface-subtle text-ink' : 'text-ink'
                    )}
                  >
                    {t('nav.signIn', 'Se connecter')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/become-partner"
                    className={cn(
                      'block px-3 py-2 rounded-lg text-sm transition-colors',
                      active ? 'bg-surface-subtle text-ink' : 'text-ink-muted'
                    )}
                  >
                    {t('nav.becomePartner', 'Devenir partenaire')}
                  </Link>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    );
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={cn(
          'inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full',
          'bg-surface-subtle hover:bg-slate-200 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30'
        )}
      >
        <span className="w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center">
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </span>
        <span className="hidden md:inline text-sm font-medium text-ink">
          {user?.name?.split(' ')[0] || t('nav.account', 'Mon compte')}
        </span>
        <ChevronDownIcon className="w-3.5 h-3.5 opacity-60" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Menu.Items
          className={cn(
            'absolute end-0 mt-2 w-52 origin-top-end',
            'bg-white border border-slate-200/60 rounded-xl shadow-elevated p-1',
            'focus:outline-none z-50'
          )}
        >
          <Menu.Item>
            {({ active }) => (
              <Link
                to="/account"
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
                  active ? 'bg-surface-subtle text-ink' : 'text-ink-muted'
                )}
              >
                <UserCircleIcon className="w-4 h-4" />
                {t('nav.account', 'Mon compte')}
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <Link
                to="/logout"
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
                  active ? 'bg-surface-subtle text-ink' : 'text-ink-muted'
                )}
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                {t('nav.logout', 'Déconnexion')}
              </Link>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function MobileMenu({ open, onClose, auth, user }) {
  const { t } = useTranslation();
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => { onClose(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [location.pathname]);

  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-0 z-[60] pointer-events-auto">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute top-0 end-0 h-full w-80 max-w-[85vw] bg-white shadow-elevated p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">Menu</span>
          <button onClick={onClose} className="p-2 -mr-2 rounded-full hover:bg-surface-subtle">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <ul className="space-y-1 mb-6">
          {[
            { to: '/', label: t('nav.home', 'Accueil'), exact: true },
            { to: '/events', label: t('nav.events', 'Activités') },
            { to: '/galleries', label: t('nav.galleries', 'Galerie') },
            { to: '/blog', label: t('nav.blog', 'Blog') },
            { to: '/contact', label: t('nav.contact', 'Contact') },
            { to: '/become-partner', label: t('nav.becomePartner', 'Devenir partenaire') },
          ].map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                exact={link.exact}
                activeClassName="!text-brand-600 !bg-brand-50"
                className="block px-3 py-2.5 rounded-lg text-base font-medium text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors"
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="border-t border-slate-100 pt-4 space-y-2">
          {auth ? (
            <>
              <Link
                to="/account"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors"
              >
                <UserCircleIcon className="w-5 h-5" />
                {user?.name || t('nav.account', 'Mon compte')}
              </Link>
              <Link
                to="/logout"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                {t('nav.logout', 'Déconnexion')}
              </Link>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => { onClose?.(); openAuthSheet(); }}
                className="block w-full text-center px-4 py-2.5 rounded-full text-sm font-semibold border border-slate-200 hover:bg-surface-subtle transition-colors"
              >
                {t('nav.signIn', 'Se connecter')}
              </button>
              <button
                type="button"
                onClick={() => { onClose?.(); openAuthSheet('signup'); }}
                className="block w-full text-center px-4 py-2.5 rounded-full text-sm font-semibold bg-brand-500 text-white hover:bg-brand-600 transition-colors"
              >
                {t('nav.signUp', 'Créer un compte')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const { auth, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authSheetOpen, setAuthSheetOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setAuthSheetOpen(true);
    window.addEventListener(AUTH_SHEET_EVENT, onOpen);
    return () => window.removeEventListener(AUTH_SHEET_EVENT, onOpen);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
    {/* The sticky bar is pointer-events-none so the page stays clickable in the
        gutters beside the floating card; children opt back in individually.
        The mobile drawer must NOT live in here — it would inherit
        pointer-events-none (making the whole menu click-through) and its
        z-[60] would be trapped inside this z-40 stacking context. */}
    <div className="sticky top-0 z-40 pointer-events-none">
      {/* Floating card layout */}
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8 pt-3 sm:pt-4">
        <nav
          className={cn(
            'pointer-events-auto',
            'flex items-center justify-between gap-6',
            'h-16 px-3 sm:px-5',
            'bg-white/85 backdrop-blur-md',
            'border border-slate-200/70',
            'rounded-2xl',
            'transition-shadow duration-300',
            scrolled ? 'shadow-elevated' : 'shadow-card'
          )}
          aria-label="Primary"
        >
          {/* LEFT — desktop primary nav OR mobile menu button */}
          <div className="flex-1 flex items-center">
            <div className="hidden lg:flex">
              <PrimaryNavLinks />
            </div>
            <button
              type="button"
              className="lg:hidden p-2 -ms-1 rounded-lg hover:bg-surface-subtle"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Bars3Icon className="w-6 h-6 text-ink" />
            </button>
          </div>

          {/* CENTER — logo (centered via equal flex-1 on both sides) */}
          <Link to="/" className="flex items-center shrink-0">
            {/* Transparent-background variant: the nav card is translucent and
                the drawer can render over dark content, so a baked-in white
                background would show as a visible block in dark mode. */}
            <img
              src="/logo-tafwija-removebg-preview.png"
              alt="Tafwija"
              className="h-9 sm:h-10 w-auto"
            />
          </Link>

          {/* RIGHT — lang + auth. No divider; hierarchy is carried by visual weight. */}
          <div className="flex-1 flex items-center justify-end gap-2 lg:gap-4">
            <LanguageSwitcher />
            <AuthCluster auth={auth} user={user} />
          </div>
        </nav>
      </div>

      {/* Standalone sign-in — same sheet the booking flow uses. No
          `orderBooking`, so nothing is ordered: on success it routes to
          the user's account. */}
      <div className="pointer-events-auto">
        <ConfirmAuthSheet open={authSheetOpen} onClose={() => setAuthSheetOpen(false)} />
      </div>
    </div>

    <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} auth={auth} user={user} />
    </>
  );
}
